// Core packages
import {
  AfterViewInit, Component, HostListener,
  OnDestroy, OnInit,
  ViewChild
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";

// Third party packages
import { MatDialog } from "@angular/material";
import { MatSidenav } from "@angular/material/sidenav";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { SidenavService } from "src/app/shared/services/sidenav.service";
import { isArchiveV3Live } from "src/app/shared/utils/archive-switch.utils";
import { archiveV3Title } from "src/app/shared/utils/archive-v3-titles.utils";
import { ArturoQueueCountService } from "src/app/shared/services/arturo-queue-count.service";
import { isArturoLive } from "src/app/shared/utils/arturo-switch.utils";
import {
  ARCHIVE_V3_DEADLINES,
  ARCHIVE_V3_GENERAL_INSERT,
  ARCHIVE_V3_GENERAL_SEARCH,
  ARCHIVE_V3_LATEST_DOCUMENTS,
  ARCHIVE_V3_NEXT_INSERTIONS,
  ARCHIVE_V3_SUMMARY,
  ARCHIVE_V3_TRASH,
  archiveV3MenuRoute,
} from "src/app/shared/utils/archive-v3-redirect.utils";
import { HelperService } from "../../services/helper.service";
import { AddCommunicationDialogComponent } from "../dialogs/add-communication-dialog/add-communication-dialog.component";

export interface SidenavItem {
  name: string;
  translate: string;
  icon?: string;
  route?: string;
  permissions: string[];
  /**
   * When true, `permissions` is evaluated with an AND instead of the default OR.
   * Needed by the entries whose back-end is protected by `hasPerms([a, b])`: showing
   * them to a user owning only one of the two leads to a page that 403s on every call.
   */
  requireAllPermissions?: boolean;
  roles?: string[];
  items?: SidenavItem[];
  hasCallbackFn?: boolean;
}

@HostListener("window:resize", ["$event"])
@Component({
  selector: "app-sidenav",
  templateUrl: "./sidenav.component.html",
  styleUrls: ["./sidenav.component.scss"]
})
export class SidenavComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  @ViewChild("sidenav", { static: false }) public sidenav: MatSidenav;
  sidenavInitialized = false;
  reason: string;
  options: any;
  loggedUser: IUser;
  loggedUserRequestedRole: string;
  roleType: string;
  loggedUserPermissions: string[];
  navItems: any[];
  activeRoute: string;
  lang: string;
  screenHeight: any;
  screenWidth: any;
  mobileScreenWidth = 768;
  hdScreenWidth = 1369;
  tankDocumentsCount = 0;
  lastInsertedDocs = 0;
  /** Arturo's open proposals, the "Da confermare" counter: 0 while unknown or not for this role */
  arturoQueueCount = 0;
  // Index of the expanded accordion panel. Typed loosely because the template stores
  // the NUMERIC *ngFor index here — (opened)="onCurrentOpened(t)" — and compares it
  // back with ===, so anything stored as a string would never match.
  currentOpened: any = '';
  subCurrentOpened: any = '';

  // @Output() createdCommunication = new EventEmitter<boolean>();

  /**
   * Class constructor
   */
  constructor(
    private sidenavService: SidenavService,
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private cookieService: CookieService,
    private configService: ConfigService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private companiesService: CompaniesService,
    private arturoQueueCountService: ArturoQueueCountService,
    public dialog: MatDialog,
  ) {
    this.getScreenSize();
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set sidenav options
    const defaultOpen =
      this.screenWidth <= this.mobileScreenWidth ? false : true;
    this.options = {
      topGap: 70,
      bottomGap: 0,
      fixed: true,
      defaultOpen
    };

    // Get logged user data
    this.loggedUser = this.authService.loggedUser$.value;

    this.subscriptions.push(this.authService.loggedUserRequestedRole$.subscribe(res => { 
      this.loggedUserRequestedRole = res; 
    }));
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(res => {
        this.loggedUserPermissions = res;
        this.updateArturoQueueCount();
      })
    );
    this.subscriptions.push(
      this.arturoQueueCountService.count$.subscribe((count: number) => {
        this.arturoQueueCount = count;
      })
    );
    this.subscriptions.push(this.authService.loggedUserRequestedRoleType$.subscribe(res => {
      this.roleType = res;
      this.updateArturoQueueCount();

      // Get nav items for sidenav menu
      this.navItems = this.getNavItems();

      if (this.roleType !== 'admin' && this.roleType !== 'operator') {
        this.subscriptions.push(
          this.authService.loggedUserRequestedRoleObj$.subscribe(res2 => {
            if (res2._id === this.loggedUserRequestedRole) {
              this.navItems = this.getNavItems();
              if (
                res2.company && 
                this.configService.companiesException.includes(res2.company._id)
              ) {
                this.navItems = this.navItems.filter((item: any) => 
                  item.name !== 'GeneralTables' &&
                  item.name !== 'Interventions' &&
                  item.name !== 'Stats'
                );
              } else {
                this.navItems = this.getNavItems();
              }

              // Suppliers: only master with valid supplier agreement 
              if (res2.type === 'master' || res2.type === 'collaborator') {
                this.subscriptions.push(this.companiesService.get(res2.company._id)
                  .subscribe((res: any) => {
                    if (res.status) {
                      if (res.data && res.data.subscription) {
                        const hasNfcAccess = !!res.data.subscription.nfcAccess;
                        const hasSuppliers = !!res.data.subscription.suppliers;

                        if (!hasNfcAccess && !hasSuppliers) {
                          this.navItems = this.navItems.filter((item: any) =>
                            item.name !== 'Suppliers'
                          );
                        } else if (hasSuppliers && !hasNfcAccess) {
                          const suppliersNav = this.navItems.find((item: any) =>
                            item.name === 'Suppliers'
                          );
                          if (
                            suppliersNav &&
                            Array.isArray(suppliersNav.items)
                          ) {
                            suppliersNav.items = suppliersNav.items.filter((item: any) => item.name !== 'Inout');
                          }
                        } else if (!hasSuppliers && hasNfcAccess) {
                          const suppliersNav = this.navItems.find((item: any) =>
                            item.name === 'Suppliers'
                          );
                          if (
                            suppliersNav &&
                            Array.isArray(suppliersNav.items)
                          ) {
                            suppliersNav.items = suppliersNav.items.filter((item: any) => item.name !== 'Search');
                          }
                        }

                        if(!res.data.subscription.documentUpload) {
                          this.navItems = this.navItems.filter((item: any) => 
                            item.name !== 'Temporaries' && 
                            // item.name !== 'Properties' && 
                            item.name !== 'GeneralTables' && 
                            item.name !== 'Communications' && 
                            item.name !== 'Deadlines' && 
                            item.name !== 'Search Docs' && 
                            item.name !== 'Access logs' && 
                            item.name !== 'Stats' && 
                            item.name !== 'Tank' && 
                            item.name !== 'HistoricalDocs'
                          );
                        }
                      }
                    }
                  })
                );
              }

              if (res2.type === 'shared') {
                const navItems = this.navItems.find((item: any) => 
                  item.roles && Array.isArray(item.roles) && item.roles.includes('shared') && item.name === 'Property'
                );
                navItems.route = `/app/properties/${res2.propertiesPermission.property[0]}/home`;
              }
            }
          })
        );
      }
    }));
    this.subscriptions.push(this.authService.tankDocumentsCount$.subscribe(res => { this.tankDocumentsCount = res; }));
    this.subscriptions.push(this.authService.historicalDocsCount$.subscribe(res => { this.lastInsertedDocs = res; }));
    this.activeRoute = this.router.routerState.snapshot.url;

    // Close sidenav on route select
    this.router.events.subscribe(() => {
      if (window.innerWidth < this.hdScreenWidth) {
        this.sidenavService.close();
      }
    });

    // Set active lang
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;
    if (this.activatedRoute.params) {
      const paramsSubscription = this.activatedRoute.params.subscribe(res => {
        if (res.lang) {
          this.lang = res.lang;
        }
      });
      this.subscriptions.push(paramsSubscription);
    }
  }

  /**
   * After view has been initialized
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    this.sidenavService.setSidenav(this.sidenav);

    // Get current route for active status
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.activeRoute = event.url;
        this.updateExpandedPanel();
        // Close sidenav on mobile after user
        // clicked on some item
        if (this.screenWidth <= this.mobileScreenWidth) {
          this.sidenav.close();
        }
      }
    });

    // Auto-expand the correct panel on initial load
    this.updateExpandedPanel();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    // Without a sidebar nobody shows the counter: no reads in the background
    this.arturoQueueCountService.stop();
  }

  /**
   * Start Arturo's "Da confermare" counter for the roles that see its entries, stop it for the others:
   * the switch of the build on, a back office role (admin or operator), Arturo's permission. Called
   * when the role or the permissions change.
   *
   * @since 3.0.0
   */
  private updateArturoQueueCount(): void {
    const backOffice = this.roleType === "admin" || this.roleType === "operator";
    const permitted = !!this.loggedUserPermissions && this.loggedUserPermissions.indexOf("arturo:use") > -1;
    if (isArturoLive() && backOffice && permitted) {
      this.arturoQueueCountService.start();
    } else {
      this.arturoQueueCountService.stop();
    }
  }

  /**
   * Handle sidenav close event closing sidenav
   *
   * @since 1.0.0
   *
   * @param reason Reason why sidenav has been closed
   */
  close(reason: string): void {
    this.reason = reason;
    this.sidenav.close();
  }

  /**
   * Get nav items
   *
   * @since 1.0.0
   *
   * @returns array of sidenavItems objects
   */
  getNavItems(): SidenavItem[] {
    // Once the 3.0.0 archive is live (FE-A1) the archive entries open its pages directly: the legacy
    // routes would lead there anyway, but the entry of the open page would not be marked (@see
    // archiveV3MenuRoute). Until then every route is the legacy one.
    const archiveLive = isArchiveV3Live();
    const items: SidenavItem[] = [
      {
        name: "Dashboard",
        translate: "sidenav.dashboard",
        icon: "fa-tachometer-alt-fastest",
        route: "/app",
        permissions: [],
        // Temporary users land on the properties list (@see HomeComponent)
        roles: ["admin", "operator", "master", "collaborator"]
      },
      {
        name: "Property",
        translate: "sidenav.my_property",
        route: "/app/properties/propertyId/home",
        icon: "fa-home",
        hasCallbackFn: true,
        permissions: [],
        roles: ["shared"]
      },
      {
        // Only company's master would see this
        name: "Company",
        translate: "sidenav.my_company",
        route: "/app/companies/my-company",
        icon: "fa-industry",
        hasCallbackFn: true,
        permissions: ['canViewCompany'],
        roles: ["master", "collaborator"]
      },
      {
        name: "Users",
        translate: "sidenav.users",
        icon: "fa-users",
        route: "/app/users",
        permissions: ["canReadUsersList", "canCreateUsers"],
        items: [
          {
            name: "List",
            translate: "sidenav.list",
            icon: "fa-chevron-right",
            route: "/app/users/list",
            permissions: ["canReadUsersList"]
          },
          {
            name: "Tagger",
            translate: "sidenav.tagger",
            icon: "fa-chevron-right",
            route: "/app/users/tagger",
            permissions: ["canReadUsersList"],
            roles: ['admin', 'operator'],
          },
          {
            name: "Add",
            translate: "sidenav.add",
            route: "/app/users/add",
            icon: "fa-chevron-right",
            permissions: ["canCreateUsers"]
          }
        ]
      },
      {
        name: "Temporaries",
        translate: "sidenav.temporaries",
        icon: "fa-sign-in-alt",
        route: "/app/temporaries",
        permissions: ["canReadTemporaryUsersList", "canCreateTemporaryUsers"],
        roles: ['master', 'collaborator'],
        items: [
          {
            name: "List",
            translate: "sidenav.temporariesList",
            icon: "fa-chevron-right",
            route: "/app/temporaries/list",
            permissions: ["canReadTemporaryUsersList"]
          },
          {
            name: "Add",
            translate: "sidenav.temporaryAdd",
            route: "/app/temporaries/add",
            icon: "fa-chevron-right",
            permissions: ["canCreateTemporaryUsers"]
          }
        ]
      },
      // {
      //   name: "Permissions",
      //   translate: "sidenav.permissions",
      //   icon: "fa-key",
      //   route: "/app/permissions",
      //   permissions: ["canReadPermissionsList", "canCreatePermissions"],
      //   roles: ["admin", "operator"],
      //   items: [
      //     {
      //       name: "List",
      //       translate: "sidenav.list",
      //       icon: "fa-list-ul",
      //       route: "/app/permissions/list",
      //       permissions: ["canReadPermissionsList"]
      //     },
      //     {
      //       name: "Add",
      //       translate: "sidenav.add",
      //       route: "/app/permissions/add",
      //       icon: "fa-plus-circle",
      //       permissions: ["canCreatePermissions"]
      //     }
      //   ]
      // },
      // {
      //   name: "Roles",
      //   translate: "sidenav.roles",
      //   icon: "fa-user-tag",roles
      //   route: "/app/roles",
      //   permissions: ["canReadRolesList", "canCreateRoles"],
      //   items: [
      //     {
      //       name: "List",
      //       translate: "sidenav.list",
      //       icon: "fa-list-ul",
      //       route: "/app/roles/list",
      //       permissions: ["canReadRolesList"]
      //     },
      //     {
      //       name: "Add",
      //       translate: "sidenav.add",
      //       route: "/app/roles/add",
      //       icon: "fa-plus-circle",
      //       permissions: ["canCreateRoles"]
      //     }
      //   ]
      // },
      {
        name: 'Companies',
        translate: 'sidenav.companies',
        icon: 'fa-industry',
        route: '/app/companies',
        permissions: ['canReadCompaniesList', 'canCreateCompanies'],
        roles: ['admin', 'operator'],
        items: [
          {
            name: "List",
            translate: "sidenav.list",
            icon: "fa-chevron-right",
            route: "/app/companies/list",
            permissions: ["canReadCompaniesList"]
          },
          {
            name: "Suspended",
            translate: "sidenav.suspended",
            icon: "fa-chevron-right",
            route: "/app/companies/suspended",
            permissions: ["canReadCompaniesList"],
            roles: ['admin', 'operator'],
          },
          {
            name: "InformativeNote",
            translate: "sidenav.informativeNote",
            icon: "fa-chevron-right",
            route: "/app/companies/informative-note",
            permissions: ["canReadCompaniesList"],
            roles: ['admin', 'operator'],
          },
          {
            name: "Add",
            translate: "sidenav.add",
            route: "/app/companies/add",
            icon: "fa-chevron-right",
            permissions: ["canCreateCompanies"]
          }
        ]
      },
      {
        name: "Properties",
        translate: "sidenav.properties",
        icon: "fa-home",
        route: "/app/properties",
        permissions: ["canReadPropertiesList", "canCreateProperties"],
        items: [
          {
            name: "List",
            translate: "sidenav.list",
            icon: "fa-chevron-right",
            route: "/app/properties/list",
            permissions: ["canReadPropertiesList"]
          },
          {
            name: "Suspended",
            translate: "sidenav.suspended",
            icon: "fa-chevron-right",
            route: "/app/properties/suspended",
            permissions: ["canReadPropertiesList"],
            roles: ['admin', 'operator'],
          },
          {
            name: "Add",
            translate: "sidenav.add",
            route: "/app/properties/add",
            icon: "fa-chevron-right",
            permissions: ["canCreateProperties"]
          },
          {
            name: "TagNfcList",
            translate: "sidenav.tagNfcList",
            icon: "fa-chevron-right",
            route: "/app/properties/tag-nfc-list",
            permissions: ["canReadPropertiesList"],
            roles: ["admin", "operator"]
          },
          
        ],
        roles: ["admin", "operator", "master", "collaborator", "temporary"]
      },
      // {
      //   name: "SuppliersAccessLogs",
      //   translate: "sidenav.suppliersAccessLogs",
      //   icon: "fa-door-open",
      //   route: "app/suppliers/",
      //   permissions: ["canReadSuppliersList"],
      //   roles: ["master", "collaborator"],
      //   items: [
      //     {
      //       name: "SuppliersAccessLogsService",
      //       translate: "sidenav.suppliersAccessLogsService",
      //       icon: "fa-chevron-right",
      //       route: "/app/suppliers/access-logs-service",
      //       permissions: ["canReadSuppliersList"]
      //     },
      //     {
      //       name: "SuppliersAccessLogsProperties",
      //       translate: "sidenav.suppliersAccessLogsProperties",
      //       icon: "fa-chevron-right",
      //       route: "/app/suppliers/access-logs-properties",
      //       permissions: ["canReadSuppliersList"]
      //     },
      //     {
      //       name: "SuppliersAccessLogsList",
      //       translate: "sidenav.suppliersAccessLogsList",
      //       icon: "fa-chevron-right",
      //       route: "/app/suppliers/access-logs-list",
      //       permissions: ["canReadSuppliersList"]
      //     },
      //   ]
      // },
      {
        name: "Suppliers",
        translate: "sidenav.suppliers",
        icon: "fa-user-hard-hat",
        route: "/app/suppliers",
        permissions: ["canReadSuppliersList", "canCreateSuppliers"],
        items: [
          // {
          //   name: "List",
          //   translate: "sidenav.list",
          //   icon: "fa-chevron-right",
          //   route: "/app/suppliers/list",
          //   permissions: ["canReadSuppliersList"],
          //   roles: ["admin", "operator"]
          // },
          {
            name: "Performer",
            translate: "sidenav.performer",
            icon: "fa-chevron-right",
            route: "/app/suppliers/performer",
            permissions: ["canReadSuppliersList"],
            roles: ["admin", "operator"]
          },
          {
            name: "Paying",
            translate: "sidenav.paying",
            icon: "fa-chevron-right",
            route: "/app/suppliers/paying",
            permissions: ["canReadSuppliersList"],
            roles: ["admin", "operator"]
          },
          {
            name: "AssessmentPanel",
            translate: "sidenav.assessment",
            icon: "fa-chevron-right",
            permissions: ["canReadSuppliersList"],
            roles: ["admin", "operator"],
            items: [
              {
                name: "Assessment",
                translate: "sidenav.list",
                icon: "fa-chevron-right",
                route: "/app/suppliers/assessment",
                permissions: ["canReadSuppliersList"],
                roles: ["admin", "operator"]
              },
              {
                name: "SupplierTank",
                translate: "sidenav.supplierTank",
                icon: "fa-chevron-right",
                route: "/app/suppliers/tank",
                permissions: ["canReadSuppliersList"],
                roles: ["admin", "operator"]
              },
              {
                name: "CleanSuppliers",
                translate: "sidenav.cleanSuppliers",
                icon: "fa-chevron-right",
                route: "/app/suppliers/cleaning",
                permissions: ["canReadSuppliersList"],
                roles: ["admin", "operator"]
              },
            ]
          },
          {
            name: "AccessRegisterPanel",
            translate: "sidenav.tagNfcList",
            icon: "fa-chevron-right",
            permissions: ["canReadSuppliersList"],
            roles: ["admin", "operator"],
            items: [
              {
                name: "invitationList",
                translate: "sidenav.list",
                route: "/app/suppliers/invitation-list",
                icon: "fa-chevron-right",
                permissions: ["canReadSuppliersList"],
                roles: ["admin", "operator"]
              },
              {
                name: "Inout",
                translate: "sidenav.tagNfcRegister",
                route: "/app/suppliers/inout",
                icon: "fa-chevron-right",
                permissions: ["canReadSuppliersList"],
                roles: ["admin", "operator"]
              },
            ]
          },

          {
            name: "ListV2",
            translate: "sidenav.list",
            icon: "fa-chevron-right",
            route: "/app/suppliers/listv2",
            permissions: ["canReadSuppliersList"],
            roles: ["master", "collaborator"]
          },
          {
            name: "Search",
            translate: "sidenav.search",
            icon: "fa-search",
            route: "/app/suppliers/search",
            permissions: ["canReadSuppliersList"],
            roles: ["master", "collaborator"]
          },
          {
            name: "Inout",
            translate: "sidenav.tagNfcRegister",
            route: "/app/suppliers/inout",
            icon: "fa-chevron-right",
            permissions: ["canReadSuppliersList"],
            roles: ["master", "collaborator"]
          },
        ],
        roles: ["admin", "operator", "master", "collaborator"]
      },
      {
        name: "GeneralTables",
        translate: "sidenav.generalTables",
        icon: "fa-table",
        route: "/app/generalTables",
        permissions: ["canReadPropertiesList"],
        roles: ["admin", "operator", "master", "collaborator"],
        items: [
          {
            name: "PersonalData",
            translate: "sidenav.personalData",
            icon: "fa-chevron-right",
            route: "/app/personal-data/list",
            permissions: ["canReadPropertiesList"]
          },
          {
            name: "Caretakers",
            translate: "sidenav.caretakers",
            icon: "fa-chevron-right",
            route: "/app/caretakers/list",
            permissions: ["canReadPropertiesList"]
          },
          {
            name: "ElectricMeters",
            translate: "sidenav.electricMeters",
            icon: "fa-chevron-right",
            route: "/app/electric-meters/list",
            permissions: ["canReadPropertiesList"]
          },
          {
            name: "GasMeters",
            translate: "sidenav.gasMeters",
            icon: "fa-chevron-right",
            route: "/app/gas-meters/list",
            permissions: ["canReadPropertiesList"]
          },
          {
            name: "WaterMeters",
            translate: "sidenav.waterMeters",
            icon: "fa-chevron-right",
            route: "/app/water-meters/list",
            permissions: ["canReadPropertiesList"]
          },
        ]
      },
      {
        name: "Communications",
        translate: "sidenav.communications",
        icon: "fa-envelope",
        route: "/app/communications",
        permissions: ["canReadCommunicationsList", "canCreateCommunications"],
        items: [
          {
            name: "Add",
            translate: "common.words.newFemale",
            icon: "fa-chevron-right",
            route: "",
            hasCallbackFn: true,
            permissions: ["canCreateCommunications"]
          },
          {
            name: "List",
            translate: "communications.sidenavList",
            icon: "fa-chevron-right",
            route: "/app/communications/list",
            permissions: ["canReadCommunicationsList"]
          },
          {
            name: "Phonebook",
            translate: "sidenav.phonebook",
            icon: "fa-chevron-right",
            route: "/app/companies/my-phonebook",
            permissions: ["canViewPhonebook"],
            // roles: ['master', 'collaborator'],
          }
        ]
      },
      {
        name: "Deadlines",
        translate: "sidenav.deadlines",
        icon: "fa-clock",
        route: "/app/deadlines",
        permissions: ["canReadPropertiesExpirationsList"],
        roles: ["admin", "operator", "master", "collaborator"],
        items: [
          {
            name: "List",
            translate: "sidenav.subscriptions",
            icon: "fa-chevron-right",
            route: "/app/deadlines/list/subscriptions",
            permissions: ["canReadPropertiesExpirationsList"]
          },
          {
            name: "List",
            translate: "sidenav.suppliersSubscriptions",
            icon: "fa-chevron-right",
            route: "/app/deadlines/list/suppliers-subscriptions",
            roles: ["admin", "operator"],
            permissions: ["canReadPropertiesExpirationsList"]
          },
          {
            name: "List",
            translate: "sidenav.nfcAccessSubscriptions",
            icon: "fa-chevron-right",
            route: "/app/deadlines/list/nfc-access-subscriptions",
            roles: ["admin", "operator"],
            permissions: ["canReadPropertiesExpirationsList"]
          },
          {
            name: "List",
            translate: "sidenav.privacySagSubscriptions",
            icon: "fa-chevron-right",
            route: "/app/deadlines/list/privacy-sag-subscriptions",
            roles: ["admin", "operator"],
            permissions: ["canReadPropertiesExpirationsList"]
          },
          {
            name: "List",
            translate: "sidenav.documents",
            icon: "fa-chevron-right",
            route: archiveV3MenuRoute(archiveLive, "/app/deadlines/list/documents", ARCHIVE_V3_DEADLINES),
            permissions: ["canReadPropertiesExpirationsList"]
          },
        ]
      },
      {
        name: "Add Documents",
        translate: "sidenav.addDocuments",
        icon: "fa-file-plus",
        route: archiveV3MenuRoute(archiveLive, "/app/add-documents", ARCHIVE_V3_GENERAL_INSERT),
        permissions: ["canCreatePaperDocuments"],
        roles: ["admin", "operator"],
      },
      {
        name: "Summary Docs",
        translate: "sidenav.summaryDocs",
        icon: "fa-chart-bar",
        // Left on the legacy route: "summary-docs-v3" starts with it, so the group opens on the 3.0.0
        // pages too
        route: "/app/summary-docs",
        permissions: ["canSearchDocuments"],
        roles: ["admin", "operator"],
        items: [
          {
            name: "List",
            translate: "sidenav.summaryDocs",
            icon: "fa-chevron-right",
            route: archiveV3MenuRoute(archiveLive, "/app/summary-docs/list", ARCHIVE_V3_SUMMARY),
            permissions: ["canSearchDocuments"]
          },
          {
            name: "InsertionsList",
            translate: "sidenav.nextInsertions",
            icon: "fa-chevron-right",
            route: archiveV3MenuRoute(archiveLive, "/app/summary-docs/insertions-list", ARCHIVE_V3_NEXT_INSERTIONS),
            permissions: ["canSearchDocuments"]
          },
        ]
      },
      {
        name: "Search Docs",
        translate: "sidenav.searchDocs",
        icon: "fa-search",
        route: archiveV3MenuRoute(archiveLive, "/app/search-docs/listv2", ARCHIVE_V3_GENERAL_SEARCH),
        permissions: ["canSearchDocuments"],
        roles: ["admin", "operator", "master", "collaborator"],
      },
      // {
      //   name: "Search Docs",
      //   translate: "sidenav.searchDocs",
      //   icon: "fa-search",
      //   route: "/app/search-docs/list",
      //   permissions: ["canSearchDocuments"],
      //   roles: ["master", "collaborator"],
      // },
      {
        name: "Ddts",
        translate: "sidenav.ddts",
        icon: "fa-clipboard-list",
        route: "/app/ddts",
        permissions: ["canReadDocumentsList"],
        roles: ["admin", "operator"],
        items: [
          {
            name: "List",
            translate: "sidenav.ddts",
            icon: "fa-chevron-right",
            route: "/app/ddts/list",
            permissions: ["canReadDocumentsList"]
          },
          {
            name: "Checklist",
            translate: "sidenav.checklist",
            route: "/app/ddts/checklist",
            icon: "fa-chevron-right",
            permissions: ["canReadDocumentsList"]
          }
        ]
      },
      // {
      //   name: "Interventions",
      //   translate: "sidenav.interventions",
      //   icon: "fa-digging",
      //   route: "/app/interventions/list",
      //   permissions: ["canReadInterventionsList", "canCreateInterventions"],
      //   roles: ["master", "collaborator"],
      //   items: [
      //     {
      //       name: "List",
      //       translate: "sidenav.list",
      //       icon: "fa-chevron-right",
      //       route: "/app/interventions/list",
      //       permissions: ["canReadInterventionsList"]
      //     },
      //     {
      //       name: "Add",
      //       translate: "sidenav.add",
      //       route: "/app/interventions/add",
      //       icon: "fa-chevron-right",
      //       permissions: ["canCreateInterventions"]
      //     }
      //   ]
      // },
      {
        name: "Balances",
        translate: "sidenav.balances",
        icon: "fa-balance-scale",
        route: "/app/balances/list",
        permissions: ["canReadBalancesList", "canCreateBalances"],
        roles: ["admin", "operator"],
        items: [
          {
            name: "First",
            translate: "sidenav.balancesFirst",
            icon: "fa-chevron-right",
            route: "/app/balances/list-first",
            permissions: ["canReadBalancesList", "canCreateBalances"]
          },
          {
            name: "Following",
            translate: "sidenav.balancesFollowing",
            icon: "fa-chevron-right",
            route: "/app/balances/list-following",
            permissions: ["canReadBalancesList", "canCreateBalances"]
          },
          {
            name: "Closed",
            translate: "sidenav.balancesClosed",
            icon: "fa-chevron-right",
            route: "/app/balances/list-closed",
            permissions: ["canReadBalancesList", "canCreateBalances"]
          },
        ]
      },
      {
        name: "Access logs",
        translate: "sidenav.access_logs",
        icon: "fa-eye",
        route: "/app/access-logs/list",
        permissions: ["canReadAccessLogsList"],
        roles: ["admin", "operator", "master", "collaborator"],
      },
      {
        // Platform staff only: every temporary user, whichever company enabled it
        name: "TemporariesReport",
        translate: "sidenav.temporariesReport",
        icon: "fa-user-clock",
        route: "/app/temporaries/report",
        permissions: ["canReadUsersList"],
        roles: ["admin", "operator"],
      },
      {
        name: "Stats",
        translate: "sidenav.stats",
        icon: "fa-chart-pie-alt",
        route: "/app/stats/list",
        permissions: ["canReadStats"],
        roles: ["admin", "operator", "master", "collaborator"],
      },
      // {
      //   name: "Groundings",
      //   translate: "sidenav.groundings",
      //   icon: "fa-circle-notch",
      //   route: "/app/groundings/list",
      //   permissions: ["canReadDocumentsList"],
      //   roles: ["admin", "operator"],
      // },
      {
        name: "Prices",
        translate: "sidenav.prices",
        icon: "fa-euro-sign",
        route: "/app/prices/list",
        permissions: ["canReadPricesList", "canCreatePrices"],
        roles: ["admin", "operator"],
        // items: [
        //   {
        //     name: "List",
        //     translate: "sidenav.list",
        //     icon: "fa-list-ul",
        //     route: "/app/prices/list",
        //     permissions: ["canReadPricesList"]
        //   }
        // ]
      },
      {
        name: "Subscriptions",
        translate: "sidenav.subscriptions",
        icon: "fa-address-card",
        route: "/app/subscriptions/list",
        permissions: ["canReadSubscriptionsList", "canCreateSubscriptions"],
        roles: ["admin", "operator"],
        // items: [
        //   {
        //     name: "List",
        //     translate: "sidenav.list",
        //     icon: "fa-list-ul",
        //     route: "/app/subscriptions/list",
        //     permissions: ["canReadSubscriptionsList"]
        //   }
        // ]
      },
      {
        name: "Tank",
        translate: "sidenav.tank",
        icon: "fa-fill-drip",
        route: "/app/tank/list",
        permissions: [],
        // permissions: ["canReadTank"],
        roles: ["master", "collaborator", "admin", "operator"],
      },
      // Arturo: only once its back-end module is on in the environment of this build (@see
      // isArturoLive), for the back office roles holding its permission
      ...(isArturoLive()
        ? [
            {
              name: "Arturo",
              translate: "arturo.sidenav.group",
              icon: "fa-magic",
              route: "/app/arturo",
              permissions: ["arturo:use"],
              roles: ["admin", "operator"],
              items: [
                {
                  // With the counter of the open proposals (@see arturoQueueCount)
                  name: "ArturoQueue",
                  translate: "arturo.sidenav.queue",
                  icon: "fa-chevron-right",
                  route: "/app/arturo/queue",
                  permissions: ["arturo:use"],
                  roles: ["admin", "operator"],
                },
                {
                  name: "ArturoUpload",
                  translate: "arturo.sidenav.upload",
                  icon: "fa-chevron-right",
                  route: "/app/arturo/upload",
                  permissions: ["arturo:use"],
                  roles: ["admin", "operator"],
                },
                {
                  name: "ArturoUploads",
                  translate: "arturo.sidenav.uploads",
                  icon: "fa-chevron-right",
                  route: "/app/arturo/uploads",
                  permissions: ["arturo:use"],
                  roles: ["admin", "operator"],
                },
                {
                  // Whether Arturo is working: its workers, pauses, jobs and queue
                  name: "ArturoStatus",
                  translate: "arturo.sidenav.status",
                  icon: "fa-chevron-right",
                  route: "/app/arturo/status",
                  permissions: ["arturo:use"],
                  roles: ["admin", "operator"],
                },
                {
                  // The thresholds and the administrators' rules: the back-end asks for BOTH keys
                  name: "ArturoSettings",
                  translate: "arturo.sidenav.settings",
                  icon: "fa-chevron-right",
                  route: "/app/arturo/settings",
                  permissions: ["arturo:use", "arturo:admin"],
                  requireAllPermissions: true,
                  roles: ["admin", "operator"],
                },
              ],
            },
          ]
        : []),
      // {
      //   name: "Generic docs",
      //   translate: "sidenav.genericDocs",
      //   icon: "fa-file",
      //   route: "/app/generic-docs/list",
      //   permissions: [],
      //   roles: ["admin", "operator"],
      // },
      {
        name: "HistoricalDocs",
        translate: "sidenav.historicalDocs",
        icon: "fa-database",
        route: archiveV3MenuRoute(archiveLive, "/app/historical-docs/list", ARCHIVE_V3_LATEST_DOCUMENTS),
        permissions: ["canViewLatestDocsInsList"],
        roles: ["admin", "operator", "master", "collaborator", "temporary"],
      },
      {
        name: "Settings",
        translate: "sidenav.settings",
        icon: "fa-cog",
        // "canRestoreDocuments" is listed here too because permissions are checked with
        // an OR: an admin who only owns that permission must still see the section.
        permissions: ["canCreateSettings", "canRestoreDocuments"],
        route: "/app/settings",
        roles: ["admin", "operator"],
        items: [
          {
            name: "Structure-detail",
            translate: "sidenav.setting_structure",
            icon: "fa-chevron-right",
            route: "/app/settings/structure-detail",
            permissions: ["canCreateSettings"]
          },
          // The editor of the old taxonomy, until the 3.0.0 archive is live: then the back-end refuses
          // its saves (410) and «Document-template» below is the only editor (FEX-27)
          ...(archiveLive
            ? []
            : [
                {
                  name: "Document-detail",
                  translate: "sidenav.setting_document",
                  icon: "fa-chevron-right",
                  route: "/app/settings/document-detail",
                  permissions: ["canCreateSettings"]
                },
              ]),
          {
            // Same archive, served by the three-collections API. Runs beside the
            // legacy entry, as «Archivio (nuovo)», until the 3.0.0 archive is live:
            // then the legacy entry is gone and this one takes its name, «Archivio»
            // (@see archiveV3Title).
            name: "Document-template",
            translate: archiveV3Title("sidenav.setting_documentTemplate", archiveLive),
            icon: "fa-chevron-right",
            route: "/app/settings/document-template",
            permissions: ["canCreateSettings"]
          },
          {
            // Data types: the forms shown when a document is archived. Its back-end
            // demands BOTH permissions (AND), hence the flag: with the default OR a
            // user owning only one of them would land on a page that 403s everywhere.
            name: "Data-types",
            translate: "sidenav.setting_dataTypes",
            icon: "fa-chevron-right",
            route: "/app/settings/data-types",
            permissions: ["canReadDocumentsList", "canCreateSettings"],
            requireAllPermissions: true,
          },
          {
            // Documents trash. Restricted to the CURRENTLY SELECTED admin role: the
            // back-end answers 403 to every other role, permission or not. Declared as
            // a leaf with a route so that it can later become a group by dropping
            // "route" and adding its own "items", with no other change.
            name: "DeletedDocs",
            translate: "sidenav.setting_documents",
            icon: "fa-chevron-right",
            route: archiveV3MenuRoute(archiveLive, "/app/deleted-docs/list", ARCHIVE_V3_TRASH),
            permissions: ["canRestoreDocuments"],
            roles: ["admin"],
          },
        ]
      },
    ];
    return items;
  }

  /**
   * Get current viewport sizes
   *
   * @since 1.0.0
   */
  getScreenSize(): void {
    this.screenHeight = window.innerHeight;
    this.screenWidth = window.innerWidth;
  }

  /**
   * Check if given item has at least one of the requested permission
   *
   * An item flagged with `requireAllPermissions` is checked with an AND instead: its
   * back-end demands every listed permission, so showing it on one of them would only
   * lead to a 403.
   *
   * @since 1.0.0
   */
  hasAtLeastOnePermisson(item: any): boolean {
    // If item does not require permission, just return true
    if (!item.permissions || !item.permissions.length) {
      return true;
    }

    if (item.requireAllPermissions) {
      return item.permissions.every(
        (perm: string) => this.loggedUserPermissions.indexOf(perm) >= 0
      );
    }

    // Check if loggedUser has at least one of the requested permissions
    const found = item.permissions.some(
      perm => this.loggedUserPermissions.indexOf(perm) >= 0
    );
    return found;
  }

  /**
   * Check if given item has at least one of the requested permission
   *
   * @since 1.0.0
   */
  hasCorrectRole(item: any): boolean {
    // If user has not received a role yet, return false
    if (!this.roleType) {
      return false;
    }

    // If item does not require roles, just return true
    if (!item.roles || !item.roles.length) {
      return true;
    }
    const found = item.roles.includes(this.roleType);
    return found;
  }

  /**
   * Handle new message dialog open
   *
   * @since 1.0.0
   */
  onAddCommunication(): void {
    let data = {};
    // If loggedUser has a company then inject company's phonebook into message dialog
    const companyId = this.cookieService.get("requestedCompany");
    if (companyId) {
      this.subscriptions.push(this.companiesService.getPhonebookList(companyId, 0, 1000, '', 'asc')
        .subscribe((res: any) => {
          if (res.status) {
            data = {
              phonebook: res.data.items,
            };
            // Open dialog
            const newMessageDialogRef = this.dialog.open(
              AddCommunicationDialogComponent,
              {
                minWidth: "70%",
                maxHeight: "98vh",
                data,
                disableClose: true
              }
            );

            // On dialog closing, check if something is changed
            // If so, let's reload table data
            this.subscriptions.push(
              newMessageDialogRef.afterClosed().subscribe(
                res => {
                  if (typeof res !== "undefined" && res.sendedMessage) {
                    this.toastrService.success(
                      this.translateService.instant(
                        "communications.add.sendedSuccessfully"
                      )
                    );
                    return;
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
            );
          }
        }));
    } else if (!companyId) {
      // Same thing but here we have no phonebook
      // Open dialog
      const newMessageDialogRef = this.dialog.open(
        AddCommunicationDialogComponent,
        {
          minWidth: "70%",
          maxHeight: "98vh",
          data,
          disableClose: true
        }
      );

      // On dialog closing, check if something is changed
      // If so, let's reload table data
      this.subscriptions.push(
        newMessageDialogRef.afterClosed().subscribe(
          res => {
            if (typeof res !== "undefined" && res.sendedMessage) {
              this.toastrService.success(
                this.translateService.instant(
                  "communications.add.sendedSuccessfully"
                )
              );
              return;
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /** Handle accordion panel open */
  onCurrentOpened(item: any): void
  {
    this.currentOpened = item;
  }

  onSubCurrentOpened(item: any): void
  {
    this.subCurrentOpened = item;
  }

  /**
   * Check if a nav item's route is active using prefix matching.
   * This ensures detail pages (e.g. /properties/123) keep the
   * parent section (e.g. /properties) highlighted.
   */
  isRouteActive(itemRoute: string): boolean {
    if (!this.activeRoute || !itemRoute) return false;
    const fullItemRoute = '/' + this.lang + itemRoute;

    // Exact match for root/dashboard route to avoid always matching
    if (itemRoute === '/app') {
      return this.activeRoute === fullItemRoute;
    }

    return this.activeRoute.startsWith(fullItemRoute);
  }

  /**
   * Auto-expand the correct accordion panel based on the current URL.
   * Iterates navItems and finds the first accordion item whose route
   * prefix matches the current activeRoute.
   *
   * Sub-items are matched too: a section can host a page that lives under a different
   * route prefix (e.g. Impostazioni > Documenti), and without this the panel holding
   * the active page would stay collapsed.
   */
  private updateExpandedPanel(): void {
    if (!this.navItems || !this.activeRoute) return;
    for (let i = 0; i < this.navItems.length; i++) {
      const item = this.navItems[i];
      if (!item.items) {
        continue;
      }
      if (item.route && this.activeRoute.startsWith('/' + this.lang + item.route)) {
        this.currentOpened = i;
        return;
      }
      const activeSubItem = item.items.find((subItem: any) =>
        subItem.route && this.activeRoute.startsWith('/' + this.lang + subItem.route)
      );
      if (activeSubItem) {
        this.currentOpened = i;
        return;
      }
    }
  }
}
